import { useCallback, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ScreenReaderAlert } from '@core/accessibility/screenReader/ScreenReaderAlert';
import { ScreenReaderOnly } from '@core/accessibility/screenReader/ScreenReaderOnly';
import { useScreenReaderAlert } from '@core/accessibility/screenReader/useScreenReaderAlert';
import { useContextTranslation } from '@core/i18n';
import { isLiveStream } from '@core/livestream/helpers';
import { Trans } from '@core/react-i18next-lazy';
import { LiveStreamingActiveUserCTAInactiveUser } from '@embed/components/VideoPlayer/Player3/components/LiveStreaming/overlays/LiveStreamingActiveUserCTA/LiveStreamingActiveUserCTAInactiveUser';
import { useActivateOrRedirectCallback } from '@embed/components/VideoPlayer/Player3/hooks/useActivateOrRedirectCallback';
import { useMuting } from '@embed/components/VideoPlayer/Player3/hooks/useMuting';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { useVideoPlayerStore } from '@embed/components/VideoPlayer/store';
import { videoPlayerIsMinimizedSelector } from '@embed/components/VideoPlayer/store/selectors';
import { AlertRound } from '@player/assets/AlertRound';
import { GATED_OVERLAY, GATED_OVERLAY_ERROR_MESSAGE, GATED_OVERLAY_INPUT, GATED_OVERLAY_SUBMIT_BUTTON, } from '@player/testIds';
import { usePlayerTracking } from '@player/tracking/usePlayerTracking';
import { useVideoGate } from '../index';
import { AccessCodeField, ErrorMessage, GateForm, GateFormButton, GateFormTitle, GateLockIcon, GateWrapper, } from './GateOverlay.styles';
import { GatePoster } from './GatePoster';
/**
 * Provides an overlay to gate a video with an acess code.
 */
export function GateOverlay() {
    const { t } = useContextTranslation();
    const accessCodeFieldRef = useRef(null);
    const gate = useVideoGate();
    const [error, setError] = useState(false);
    const isMinimized = useVideoPlayerStore(videoPlayerIsMinimizedSelector);
    const video = useStore((state) => state.video);
    const setSRAlert = useScreenReaderAlert();
    const errorMessage = t('player:Incorrect access code');
    const [checkingAccessCode, setCheckingAccessCode] = useState(false);
    const { unmute } = useMuting();
    const isVideoTypeLiveStream = isLiveStream(video);
    const onClick = useActivateOrRedirectCallback({
        onClick: useCallback(() => {
            unmute();
        }, [unmute]),
    });
    const { trackGatedVideoCorrectAccessCode, trackGatedVideoIncorrectAccessCode, } = usePlayerTracking();
    const tryUnlock = (e) => {
        if (video) {
            setCheckingAccessCode(true);
            gate
                .unlock(accessCodeFieldRef.current?.value || '', video.encoded_id, video.live_stream_id)
                .then((unlocked) => {
                setCheckingAccessCode(false);
                setError(!unlocked);
                if (unlocked) {
                    setSRAlert(t('player:Video unlocked'));
                    if (isVideoTypeLiveStream) {
                        onClick(e);
                    }
                    else {
                        unmute();
                    }
                    trackGatedVideoCorrectAccessCode();
                }
                else {
                    trackGatedVideoIncorrectAccessCode();
                }
            });
        }
    };
    return (<GateWrapper role="dialog" aria-labelledby="fw-GateOverlayTitle" data-testid={GATED_OVERLAY}>
      <GatePoster video={video}/>
      {!isMinimized ? (<GateForm>
          <GateLockIcon width={90} aria-hidden/>
          <GateFormTitle id="fw-GateOverlayTitle">
            {isVideoTypeLiveStream ? (<Trans ns="player" i18nKey="Enter Access Code <0>to unlock this livestream</0>" defaults="Enter Access Code <0>to unlock this livestream</0>" components={[<ScreenReaderOnly key="0"/>]}/>) : (<Trans ns="player" i18nKey="Enter Access Code <0>to unlock this video</0>" defaults="Enter Access Code <0>to unlock this video</0>" components={[<ScreenReaderOnly key="0"/>]}/>)}
          </GateFormTitle>
          <AccessCodeField aria-label={t('player:Enter the access code for this video')} error={error} type="text" placeholder={t('player:Access Code')} required ref={accessCodeFieldRef} data-testid={GATED_OVERLAY_INPUT} onKeyUp={(e) => {
                if (e.key === 'Enter') {
                    tryUnlock(e);
                }
            }} onChange={() => {
                if (error) {
                    setError(false);
                }
            }}/>
          <ErrorMessage data-testid={GATED_OVERLAY_ERROR_MESSAGE}>
            {error ? (<>
                <AlertRound height={18} width={18} css={css `
                    display: inline-block;
                    margin-right: 4px;
                  `} label="" aria-hidden/>

                <ScreenReaderAlert visible={true}>
                  {errorMessage}
                </ScreenReaderAlert>
              </>) : null}
          </ErrorMessage>
          <GateFormButton variant="primary" onClick={tryUnlock} isLoading={checkingAccessCode} onKeyUp={(e) => {
                if (e.key === 'Enter') {
                    tryUnlock(e);
                }
            }} data-testid={GATED_OVERLAY_SUBMIT_BUTTON}>
            {t(`player:Watch`)}
          </GateFormButton>
        </GateForm>) : (<div style={{ color: 'white' }}>
          <LiveStreamingActiveUserCTAInactiveUser />
        </div>)}
    </GateWrapper>);
}
export default GateOverlay;
